/**
 * The customer's service programs (bundles) in the portal account view (tracker item 85): one card per
 * enrollment with its timeline (item, planned / scheduled / completed date, status, price), progress, the next
 * visit, the program total and an Auto-renew switch. The switch only controls whether a renewal REQUEST is
 * prepared for next season; it never approves, schedules or charges anything. Customer-facing data only: the API
 * sends no technician, cost, invoice or renewal-price fields.
 */
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import * as types from '../../api/types';
import { primaryButtonClass } from '../../components/formStyles';
import { ProgressBar } from '../../features/service-bundles/EnrollmentTimeline';
import { nextVisitText, progressText } from '../../features/service-bundles/bundleUi';
import { PORTAL_REQUEST_SERVICE_PATH, localToday, portalAddress, portalDay } from '../../utils/portal';
import { invoiceMoney } from '../invoiceUi';
import { usePortal, usePortalApi } from './PortalContext';
import { EmptyBlock, FailureBlock, InlineAlert, LoadingBlock } from './PortalStates';
import { usePortalLoad } from './usePortalLoad';
import {
  PROGRAM_STATUS_LABELS, VISIT_STATUS_LABELS, describeAutoRenewFailure, programStatusClass, programStatusNote, visitStatusClass,
} from './portalBundleUi';

/** The date a visit shows: when it was done, else the scheduled date (the planned one before the visit is booked). */
function visitDate(step: types.PortalEnrollmentStep): string {
  return step.status === 'COMPLETED' && step.completedDate ? step.completedDate : step.scheduledDate;
}

function visitDateLabel(step: types.PortalEnrollmentStep): string {
  if (step.status === 'COMPLETED') return `Done ${portalDay(visitDate(step))}`;
  if (step.status === 'REQUESTED' || step.status === 'PLANNED') return `Planned for ${portalDay(visitDate(step))}`;
  return portalDay(visitDate(step));
}

export function PortalProgramTimeline({ enrollment }: { enrollment: types.PortalEnrollment }) {
  return (
    <ol className="mt-3" aria-label={`${enrollment.bundleName} visits`}>
      {enrollment.steps.map((step, index) => {
        const last = index === enrollment.steps.length - 1;
        return (
          <li key={step.step} className="relative flex gap-3 pb-4" data-testid={`program-step-${step.step}`}>
            {!last && <span aria-hidden="true" className="absolute left-3.5 top-7 h-full w-px -translate-x-1/2 bg-gray-200" />}
            <span
              aria-hidden="true"
              className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${step.status === 'COMPLETED' ? 'bg-green-600 text-white' : step.status === 'CANCELLED' ? 'bg-gray-400 text-white' : 'bg-white text-gray-600 ring-2 ring-gray-300'}`}
            >
              {step.step}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="break-words font-semibold text-gray-900">
                  {step.itemName}
                  {Number(step.quantity) !== 1 && <span className="ml-1 font-normal text-gray-600">× {step.quantity}</span>}
                </p>
                <p className="text-sm font-medium text-gray-900">{invoiceMoney(step.lineTotal)}</p>
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                <span data-testid="program-step-date">{visitDateLabel(step)}</span>
                <span className={`rounded px-2 py-0.5 text-xs font-semibold ${visitStatusClass(step.status)}`} data-testid="program-step-status">{VISIT_STATUS_LABELS[step.status]}</span>
              </p>
              {step.jobId && step.status !== 'CANCELLED' && (
                <Link to={`/portal/jobs/${step.jobId}`} className="mt-1 inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">View visit</Link>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ProgramCard({ initial, companyName }: { initial: types.PortalEnrollment; companyName: string }) {
  const api = usePortalApi();
  const { reportFailure } = usePortal();
  const [program, setProgram] = useState(initial);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const cancelled = program.status === 'CANCELLED';
  const note = programStatusNote(program, companyName);
  const next = program.status === 'ACTIVE' ? nextVisitText(program.nextVisitDate, localToday()) : null;

  const toggle = async (value: boolean) => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      setProgram(await api.setPortalEnrollmentAutoRenew(program.id, value));
    } catch (cause) {
      const described = describeAutoRenewFailure(cause);
      if (described.kind === 'session') reportFailure({ kind: 'session', message: described.message });
      setProblem(described.message);
      if (described.reload) {
        try { setProgram(await api.getPortalEnrollment(program.id)); } catch { /* the message above stays */ }
      }
    } finally {
      setBusy(false);
    }
  };

  const headingId = `program-${program.id}`;
  return (
    <li className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" data-testid="program-card" aria-labelledby={headingId}>
      <div className="flex items-start justify-between gap-3">
        <h2 id={headingId} className="min-w-0 break-words text-base font-bold text-gray-900">{program.bundleName}</h2>
        <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${programStatusClass(program.status)}`} data-testid="program-status">{PROGRAM_STATUS_LABELS[program.status]}</span>
      </div>
      <p className="mt-1 break-words text-sm text-gray-600">{portalAddress({ ...program.property, zip: '' })}</p>
      {note && <p className="mt-2 rounded-md bg-blue-50 p-2 text-sm text-blue-900" data-testid="program-note">{note}</p>}

      {program.status !== 'PENDING_APPROVAL' && !cancelled && (
        <div className="mt-3"><ProgressBar progress={program.progress} label={progressText(program.progress)} /></div>
      )}
      {next && <p className="mt-2 text-sm font-medium text-blue-800" data-testid="program-next">{next}</p>}
      <p className="mt-2 flex justify-between gap-3 text-sm text-gray-900">
        <span>{program.status === 'PENDING_APPROVAL' ? 'Estimated total (before tax)' : 'Program total (before tax)'}</span>
        <span className="font-semibold" data-testid="program-total">{invoiceMoney(program.total)}</span>
      </p>

      <button type="button" className="mt-2 min-h-[44px] text-sm font-semibold text-blue-700 hover:underline" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide visits' : `Show ${program.stepCount} visit${program.stepCount === 1 ? '' : 's'}`}
      </button>
      {open && <PortalProgramTimeline enrollment={program} />}

      <div className="mt-3 border-t pt-3">
        <div className="flex items-start gap-3">
          <input
            id={`${headingId}-autorenew`} type="checkbox" role="switch" className="mt-1 h-5 w-5" checked={program.autoRenew} aria-checked={program.autoRenew}
            disabled={busy || cancelled} onChange={(e) => void toggle(e.target.checked)} aria-describedby={`${headingId}-autorenew-help`}
          />
          <label htmlFor={`${headingId}-autorenew`} className="text-sm font-medium text-gray-900">
            Auto-renew
            <span id={`${headingId}-autorenew-help`} className="block text-xs font-normal text-gray-600" data-testid="autorenew-help">
              This only controls whether we prepare a request for next season's renewal when this program ends. It never approves, schedules or charges anything:
              {' '}{companyName} reviews every renewal with you first.
            </span>
          </label>
        </div>
        {problem && <div className="mt-2"><InlineAlert>{problem}</InlineAlert></div>}
      </div>
    </li>
  );
}

export function PortalProgramsPage() {
  const { me } = usePortal();
  const api = usePortalApi();
  const load = useCallback(() => api.listPortalEnrollments({ limit: 50 }), [api]);
  const { data, loading, failure, reload } = usePortalLoad(load, 'We could not load your service programs.');

  if (loading && !data) return <LoadingBlock label="Loading your programs" />;
  if (failure) return <FailureBlock failure={failure} onRetry={failure.kind === 'not-found' ? undefined : () => void reload()} />;

  const programs = data?.data ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-bold text-gray-900">Your service programs</h1>
        <Link to={PORTAL_REQUEST_SERVICE_PATH} className={`${primaryButtonClass} inline-flex min-h-[44px] items-center justify-center`}>Request a program</Link>
      </div>
      {programs.length === 0 ? (
        <EmptyBlock>You do not have a service program yet. You can ask for one from the Request service page.</EmptyBlock>
      ) : (
        <ul className="space-y-4" aria-label="Service programs">
          {programs.map((program) => <ProgramCard key={program.id} initial={program} companyName={me.companyName} />)}
        </ul>
      )}
    </div>
  );
}
